import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { clientsService, workflowStagesService, paymentLinksService } from '../../services';
import { useResponsive } from '../../hooks/useResponsive';
import CommunicationLogger from './CommunicationLogger';
import travelieTheme from '../../styles/travelieTheme';
import Papa from 'papaparse';
import ClientRegistrationLink from './ClientRegistrationLink';

// Phase 8: Completed TravelieTheme Migration - June 18, 2026

// Member Card Component for displaying and editing additional members
const MemberCard = ({ member, index, onEdit, onRemove }) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({ ...member });

  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };

  const labelStyle = {
    display: 'block',
    marginBottom: travelieTheme.spacing.xs,
    fontSize: travelieTheme.typography.fontSize.sm,
    fontWeight: travelieTheme.typography.fontWeight.medium,
    color: travelieTheme.colors.text.primary
  };

  // Helper function for button styling
  const getButtonStyle = (type, size = 'medium', disabled = false) => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: '#EF4444', color: '#FFFFFF' },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };

  const handleSave = () => {
    onEdit(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData({ ...member });
    setIsEditing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle YYYY-MM-DD format directly to avoid timezone issues
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString();
      }
      // Fallback for other date formats
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{ background: "#F9FAFB", borderRadius: travelieTheme.components.card.borderRadius, border: `1px solid ${travelieTheme.colors.neutral.border}` }}>
      {/* Header - Always visible */}
      <div style={{ padding: travelieTheme.spacing.md, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.sm }}>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ color: travelieTheme.colors.text.secondary, cursor: "pointer", transition: `color ${travelieTheme.transitions.fast}` }}
          >
            <svg style={{ width: "16px", height: "16px", transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: `transform ${travelieTheme.transitions.fast}` }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <span style={{ fontWeight: travelieTheme.typography.fontWeight.medium }}>
            {member.first_name} {member.last_name}
            {member.email && <span style={{ color: travelieTheme.colors.text.secondary, fontWeight: travelieTheme.typography.fontWeight.normal }}> ({member.email})</span>}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: travelieTheme.spacing.sm }}>
          <button
            type="button"
            onClick={() => setIsEditing(!isEditing)}
            style={{ color: travelieTheme.colors.primary.main, fontSize: travelieTheme.typography.fontSize.sm, cursor: "pointer", transition: `color ${travelieTheme.transitions.fast}` }}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </button>
          <button
            type="button"
            onClick={onRemove}
            style={{ color: "#DC2626", fontSize: travelieTheme.typography.fontSize.sm, cursor: "pointer", transition: `color ${travelieTheme.transitions.fast}` }}
          >
            Remove
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div style={{ padding: `0 ${travelieTheme.spacing.md} ${travelieTheme.spacing.md}`, borderTop: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          {isEditing ? (
            /* Edit Mode */
            <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md, paddingTop: travelieTheme.spacing.md }}>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md }}>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>First Name *</label>
                  <input
                    type="text"
                    value={editData.first_name || ''}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Last Name *</label>
                  <input
                    type="text"
                    value={editData.last_name || ''}
                    onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Email</label>
                  <input
                    type="email"
                    value={editData.email || ''}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Phone</label>
                  <input
                    type="tel"
                    value={editData.phone || ''}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Date of Birth</label>
                  <input
                    type="date"
                    value={editData.date_of_birth || ''}
                    onChange={(e) => setEditData({ ...editData, date_of_birth: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Passport Number</label>
                  <input
                    type="text"
                    value={editData.passport_number || ''}
                    onChange={(e) => setEditData({ ...editData, passport_number: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Passport Expiry</label>
                  <input
                    type="date"
                    value={editData.passport_expiry || ''}
                    onChange={(e) => setEditData({ ...editData, passport_expiry: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
                <div>
                  <label style={{...labelStyle, fontSize: travelieTheme.typography.fontSize.xs}}>Passport Country</label>
                  <input
                    type="text"
                    value={editData.passport_country || ''}
                    onChange={(e) => setEditData({ ...editData, passport_country: e.target.value })}
                    style={{...inputStyle, fontSize: travelieTheme.typography.fontSize.sm}}
                  />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.sm, paddingTop: travelieTheme.spacing.sm }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={getButtonStyle("secondary", "small")}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!editData.first_name || !editData.last_name}
                  style={getButtonStyle("primary", "small")}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            /* View Mode */
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, paddingTop: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.sm }}>
              <div>
                <span style={{ color: travelieTheme.colors.text.secondary }}>Phone:</span>
                <span style={{ marginLeft: travelieTheme.spacing.sm }}>{member.phone || 'Not provided'}</span>
              </div>
              <div>
                <span style={{ color: travelieTheme.colors.text.secondary }}>Date of Birth:</span>
                <span style={{ marginLeft: travelieTheme.spacing.sm }}>{member.date_of_birth ? formatDate(member.date_of_birth) : 'Not provided'}</span>
              </div>
              <div>
                <span style={{ color: travelieTheme.colors.text.secondary }}>Passport Number:</span>
                <span style={{ marginLeft: travelieTheme.spacing.sm }}>{member.passport_number || 'Not provided'}</span>
              </div>
              <div>
                <span style={{ color: travelieTheme.colors.text.secondary }}>Passport Expiry:</span>
                <span style={{ marginLeft: travelieTheme.spacing.sm }}>{member.passport_expiry ? formatDate(member.passport_expiry) : 'Not provided'}</span>
              </div>
              <div style={{ gridColumn: isMobile ? "span 1" : "span 2" }}>
                <span style={{ color: travelieTheme.colors.text.secondary }}>Passport Country:</span>
                <span style={{ marginLeft: travelieTheme.spacing.sm }}>{member.passport_country || 'Not provided'}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Separate form component to prevent re-renders
const ClientFormComponent = ({ isEdit, initialData, onSubmit, onCancel, formLoading }) => {
  const { isMobile } = useResponsive();
  const formRef = useRef(null);
  const [additionalMembers, setAdditionalMembers] = useState(initialData?.additional_members || []);
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    passport_number: '',
    passport_expiry: '',
    passport_country: ''
  });

  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };

  const labelStyle = {
    display: 'block',
    marginBottom: travelieTheme.spacing.xs,
    fontSize: travelieTheme.typography.fontSize.sm,
    fontWeight: travelieTheme.typography.fontWeight.medium,
    color: travelieTheme.colors.text.primary
  };

  // Helper function for button styling
  const getButtonStyle = (type, size = 'medium', disabled = false) => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: '#EF4444', color: '#FFFFFF' },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };
  
  // Initialize form with initial data only once
  useEffect(() => {
    if (formRef.current && initialData) {
      const form = formRef.current;
      Object.keys(initialData).forEach(key => {
        if (form[key] && key !== 'additional_members') {
          // Handle date fields properly - ensure they're in YYYY-MM-DD format
          if (key === 'date_of_birth' || key === 'passport_expiry') {
            const dateValue = initialData[key];
            if (dateValue) {
              // Convert date to YYYY-MM-DD format if needed
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                form[key].value = date.toISOString().split('T')[0];
              } else {
                form[key].value = dateValue;
              }
            } else {
              form[key].value = '';
            }
          } else {
            form[key].value = initialData[key] || '';
          }
        }
      });
      
      // Load existing additional members when editing - handle various data formats
      let membersToLoad = [];
      if (initialData.additional_members) {
        if (Array.isArray(initialData.additional_members)) {
          membersToLoad = initialData.additional_members;
        } else if (typeof initialData.additional_members === 'string') {
          try {
            const parsed = JSON.parse(initialData.additional_members);
            membersToLoad = Array.isArray(parsed) ? parsed : [];
          } catch (e) {
            console.warn('Failed to parse additional_members JSON:', e);
            membersToLoad = [];
          }
        }
      }
      setAdditionalMembers(membersToLoad);
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Add additional members to the data
    data.additional_members = additionalMembers;
    
    onSubmit(data);
  };

  const addMemberToGroup = () => {
    if (newMember.first_name && newMember.last_name) {
      setAdditionalMembers([...additionalMembers, { ...newMember }]);
      setNewMember({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: '',
        passport_country: ''
      });
    }
  };

  const removeMemberFromGroup = (index) => {
    setAdditionalMembers(additionalMembers.filter((_, i) => i !== index));
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xl }}>
      {/* Lead Traveler Information */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Lead Traveler Information</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.lg }}>
          <div>
            <label style={labelStyle}>First Name *</label>
            <input
              type="text"
              name="first_name"
              required
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Last Name *</label>
            <input
              type="text"
              name="last_name"
              required
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Email *</label>
            <input
              type="email"
              name="email"
              required
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Phone *</label>
            <input
              type="tel"
              name="phone"
              required
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
        </div>
      </div>

      {/* Optional Information */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Optional Information</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.lg }}>
          <div style={{ gridColumn: isMobile ? "span 1" : "span 2" }}>
            <label style={labelStyle}>Address</label>
            <input
              type="text"
              name="address"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Date of Birth</label>
            <input
              type="date"
              name="date_of_birth"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Passport Number</label>
            <input
              type="text"
              name="passport_number"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Passport Expiry</label>
            <input
              type="date"
              name="passport_expiry"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Passport Country</label>
            <input
              type="text"
              name="passport_country"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
        </div>
      </div>

      {/* Emergency Contact */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Emergency Contact</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: travelieTheme.spacing.lg }}>
          <div>
            <label style={labelStyle}>Contact Name</label>
            <input
              type="text"
              name="emergency_contact_name"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Contact Phone</label>
            <input
              type="tel"
              name="emergency_contact_phone"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Relationship</label>
            <input
              type="text"
              name="emergency_contact_relation"
              disabled={formLoading}
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
        </div>
      </div>

      {/* Loyalty Programs */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Loyalty Programs</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: travelieTheme.spacing.lg }}>
          <div>
            <label style={labelStyle}>Hotel Loyalty Number</label>
            <input
              type="text"
              name="hotel_loyalty_number"
              disabled={formLoading}
              placeholder="e.g., Marriott Bonvoy, Hilton Honors"
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Airline Loyalty Number</label>
            <input
              type="text"
              name="airline_loyalty_number"
              disabled={formLoading}
              placeholder="e.g., Delta SkyMiles, United MileagePlus"
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
          <div>
            <label style={labelStyle}>Cruise Loyalty Number</label>
            <input
              type="text"
              name="cruise_loyalty_number"
              disabled={formLoading}
              placeholder="e.g., Royal Caribbean Crown & Anchor"
              style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
            />
          </div>
        </div>
      </div>

      {/* Additional Members - Available for both new and existing clients */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Additional Group Members</h3>
        
        {/* List of added members */}
        {Array.isArray(additionalMembers) && additionalMembers.length > 0 && (
          <div style={{ marginBottom: travelieTheme.spacing.lg, display: "flex", flexDirection: "column", gap: travelieTheme.spacing.md }}>
            {additionalMembers.map((member, index) => (
              <MemberCard
                key={index}
                member={member}
                index={index}
                onEdit={(updatedMember) => {
                  const updated = [...additionalMembers];
                  updated[index] = updatedMember;
                  setAdditionalMembers(updated);
                }}
                onRemove={() => removeMemberFromGroup(index)}
              />
            ))}
          </div>
        )}
        
        {/* Add new member form */}
        <div style={{ borderTop: `1px solid ${travelieTheme.colors.neutral.border}`, paddingTop: travelieTheme.spacing.lg }}>
          <h4 style={{...labelStyle, marginBottom: travelieTheme.spacing.md}}>Add New Member</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md }}>
            <input
              type="text"
              value={newMember.first_name}
              onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
              placeholder="First Name *"
              style={inputStyle}
            />
            <input
              type="text"
              value={newMember.last_name}
              onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
              placeholder="Last Name *"
              style={inputStyle}
            />
            <input
              type="email"
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              placeholder="Email (optional)"
              style={inputStyle}
            />
            <input
              type="tel"
              value={newMember.phone}
              onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
              placeholder="Phone (optional)"
              style={inputStyle}
            />
            <input
              type="date"
              value={newMember.date_of_birth}
              onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
              placeholder="Date of Birth (optional)"
              style={inputStyle}
            />
            <input
              type="text"
              value={newMember.passport_number}
              onChange={(e) => setNewMember({ ...newMember, passport_number: e.target.value })}
              placeholder="Passport Number (optional)"
              style={inputStyle}
            />
            <input
              type="date"
              value={newMember.passport_expiry}
              onChange={(e) => setNewMember({ ...newMember, passport_expiry: e.target.value })}
              placeholder="Passport Expiry (optional)"
              style={inputStyle}
            />
            <input
              type="text"
              value={newMember.passport_country}
              onChange={(e) => setNewMember({ ...newMember, passport_country: e.target.value })}
              placeholder="Passport Country (optional)"
              style={inputStyle}
            />
          </div>
          <button
            type="button"
            onClick={addMemberToGroup}
            disabled={!newMember.first_name || !newMember.last_name}
            style={{...getButtonStyle("primary"), marginTop: travelieTheme.spacing.md}}
          >
            Add Member
          </button>
        </div>
      </div>

      {/* Agent Notes */}
      <div>
        <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary, marginBottom: travelieTheme.spacing.lg }}>Agent Notes</h3>
        <textarea
          name="agent_notes"
          rows={4}
          disabled={formLoading}
          placeholder="Internal notes about the client (not visible to client)"
          style={{...inputStyle, marginTop: travelieTheme.spacing.xs}}
        />
      </div>

      {/* Form Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.md }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={formLoading}
          style={getButtonStyle("secondary")}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={formLoading}
          style={getButtonStyle("primary", "medium", false)}
        >
          {formLoading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
        </button>
      </div>
    </form>
  );
};

const ClientsManagement = ({ clients: initialClients, onUpdateClients }) => {
  const { isMobile, isDesktopOrWide } = useResponsive();
  
  // Helper function for button styling
  const getButtonStyle = (type, size = 'medium', disabled = false) => {
    const baseStyle = {
      padding: size === 'small' ? `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}` : `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: size === 'small' ? travelieTheme.typography.fontSize.xs : travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1,
      display: 'inline-flex',
      alignItems: 'center',
      gap: travelieTheme.spacing.xs
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary },
      danger: { ...baseStyle, background: '#EF4444', color: '#FFFFFF' },
      success: { ...baseStyle, background: travelieTheme.colors.status.success, color: '#FFFFFF' }
    };

    return styles[type] || styles.primary;
  };

  // Helper styles for form elements
  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };

  const labelStyle = {
    display: 'block',
    marginBottom: travelieTheme.spacing.xs,
    fontSize: travelieTheme.typography.fontSize.sm,
    fontWeight: travelieTheme.typography.fontWeight.medium,
    color: travelieTheme.colors.text.primary
  };

  const [clients, setClients] = useState(initialClients || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Workflow stages state
  const [workflowStages, setWorkflowStages] = useState([]);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  
  // CSV Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Communication logging state
  const [showCommLogger, setShowCommLogger] = useState(false);
  const [commLoggerClient, setCommLoggerClient] = useState(null);

  useEffect(() => {
    setClients(initialClients || []);
  }, [initialClients]);

  useEffect(() => {
    if (!initialClients) {
      fetchClients();
    }
    fetchWorkflowStages();
  }, []);

  const fetchWorkflowStages = async () => {
    try {
      const stages = await workflowStagesService.getStages();
      setWorkflowStages(stages);
    } catch (error) {
      console.error('Failed to fetch workflow stages:', error);
    }
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsService.getAll();
      setClients(data);
      if (onUpdateClients) {
        onUpdateClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setError('Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = () => {
    setEditingClient(null);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowModal(true);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await clientsService.delete(clientId);
        const updatedClients = clients.filter(c => c.id !== clientId);
        setClients(updatedClients);
        if (onUpdateClients) {
          onUpdateClients(updatedClients);
        }
        setSuccess('Client deleted successfully');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Error deleting client:', error);
        setError('Failed to delete client. Please try again.');
      }
    }
  };

  const handleRequestPayment = (client) => {
    // Pre-fill payment request form with client data
    const paymentRequestUrl = `/agent/payment-links?client=${encodeURIComponent(JSON.stringify({
      id: client.id,
      name: `${client.first_name} ${client.last_name}`,
      email: client.email,
      phone: client.phone
    }))}`;
    
    // Open payment links page with pre-filled client data
    window.location.href = paymentRequestUrl;
  };

  const handleSubmitClient = async (formData) => {
    try {
      setFormLoading(true);
      setError(null);
      
      if (editingClient) {
        await clientsService.update(editingClient.id, formData);
        setSuccess('Client updated successfully');
      } else {
        await clientsService.create(formData);
        setSuccess('Client created successfully');
      }
      
      // Refetch clients to ensure data consistency and immediate UI update
      await fetchClients();
      
      setShowModal(false);
      setEditingClient(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving client:', error);
      setError(error.response?.data?.message || 'Failed to save client. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  // CSV Import Functions
  const handleDownloadTemplate = async () => {
    try {
      await clientsService.downloadTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
      setError('Failed to download template. Please try again.');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      
      // Parse CSV for preview
      Papa.parse(file, {
        header: true,
        preview: 5, // Show first 5 rows for preview
        complete: (results) => {
          setCsvPreview(results.data);
        },
        error: (error) => {
          console.error('CSV parsing error:', error);
          setError('Failed to parse CSV file. Please check the format.');
        }
      });
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      setError('Please select a CSV file to import.');
      return;
    }

    try {
      setImportLoading(true);
      setError(null);
      
      const results = await clientsService.bulkImport(csvFile);
      setImportResults(results);
      
      // Refresh clients list
      await fetchClients();
      
      if (results.summary.successful > 0) {
        setSuccess(`Successfully imported ${results.summary.successful} clients!`);
        setTimeout(() => setSuccess(null), 5000);
      }
      
    } catch (error) {
      console.error('CSV import error:', error);
      setError(error.response?.data?.error || 'Failed to import CSV. Please try again.');
    } finally {
      setImportLoading(false);
    }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setCsvFile(null);
    setCsvPreview([]);
    setImportResults(null);
    setError(null);
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = searchTerm === '' || 
      `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const handleMoveClientStage = async (clientId, stageId, reason = null) => {
    try {
      await workflowStagesService.moveClient(clientId, stageId, reason);
      setSuccess('Client moved to new stage successfully');
      await fetchClients();
      setShowStageModal(false);
      setSelectedClient(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Failed to move client:', error);
      setError('Failed to move client to new stage. Please try again.');
    }
  };

  if (loading && !initialClients) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "256px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ animation: "spin 1s linear infinite", borderRadius: "50%", height: "48px", width: "48px", borderBottom: "2px solid " + travelieTheme.colors.primary.main, margin: "0 auto" }}></div>
          <p style={{ marginTop: travelieTheme.spacing.lg, color: travelieTheme.colors.text.secondary }}>Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: travelieTheme.spacing['2xl'] }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <div>
          <h2 style={{
            fontSize: travelieTheme.typography.fontSize['2xl'],
            fontWeight: travelieTheme.typography.fontWeight.bold,
            color: travelieTheme.colors.text.primary,
            margin: 0
          }}>Clients</h2>
          <p style={{
            marginTop: travelieTheme.spacing.xs,
            fontSize: travelieTheme.typography.fontSize.sm,
            color: travelieTheme.colors.text.secondary
          }}>
            Manage your client database
          </p>
        </div>
        <div style={{
          marginTop: isMobile ? travelieTheme.spacing.lg : 0,
          display: 'flex',
          gap: travelieTheme.spacing.sm,
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleCreateClient}
            style={{
              ...travelieTheme.components.button.primary,
              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
              background: travelieTheme.colors.primary.main,
              color: travelieTheme.colors.text.white,
              border: 'none',
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              fontSize: travelieTheme.typography.fontSize.sm,
              fontWeight: travelieTheme.typography.fontWeight.medium,
              cursor: 'pointer',
              transition: `background ${travelieTheme.transitions.normal}`
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.primary.hover}
            onMouseLeave={(e) => e.currentTarget.style.background = travelieTheme.colors.primary.main}
          >
            Add New Client
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            style={{
              padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
              background: travelieTheme.colors.status.success,
              color: travelieTheme.colors.text.white,
              border: 'none',
              borderRadius: travelieTheme.components.button.primary.borderRadius,
              fontSize: travelieTheme.typography.fontSize.sm,
              fontWeight: travelieTheme.typography.fontWeight.medium,
              cursor: 'pointer',
              transition: `background ${travelieTheme.transitions.normal}`
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#0ea371'}
            onMouseLeave={(e) => e.currentTarget.style.background = travelieTheme.colors.status.success}
          >
            Import Clients
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          background: '#FEF2F2',
          border: `1px solid #FECACA`,
          color: '#DC2626',
          padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          marginBottom: travelieTheme.spacing.lg,
          fontSize: travelieTheme.typography.fontSize.sm
        }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{
          background: travelieTheme.colors.status.successLight,
          border: `1px solid #BBF7D0`,
          color: travelieTheme.colors.status.success,
          padding: `${travelieTheme.spacing.md} ${travelieTheme.spacing.lg}`,
          borderRadius: travelieTheme.components.card.borderRadius,
          marginBottom: travelieTheme.spacing.lg,
          fontSize: travelieTheme.typography.fontSize.sm
        }}>
          {success}
        </div>
      )}

      {/* Search */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: travelieTheme.spacing.lg,
        marginBottom: travelieTheme.spacing['2xl']
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <svg style={{
              position: 'absolute',
              left: travelieTheme.spacing.md,
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px',
              height: '20px',
              color: travelieTheme.colors.text.secondary
            }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search clients by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: `${travelieTheme.spacing.sm} ${searchTerm ? travelieTheme.spacing['3xl'] : travelieTheme.spacing.lg} ${travelieTheme.spacing.sm} ${travelieTheme.spacing['3xl']}`,
                border: `1px solid ${travelieTheme.colors.neutral.border}`,
                borderRadius: travelieTheme.components.input.borderRadius,
                fontSize: travelieTheme.typography.fontSize.sm,
                outline: 'none',
                transition: `border-color ${travelieTheme.transitions.fast}`
              }}
              onFocus={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.primary.main}
              onBlur={(e) => e.currentTarget.style.borderColor = travelieTheme.colors.neutral.border}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: travelieTheme.spacing.sm,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: travelieTheme.colors.text.secondary,
                  padding: travelieTheme.spacing.xs,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: `all ${travelieTheme.transitions.fast}`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = travelieTheme.colors.neutral.hover;
                  e.currentTarget.style.color = travelieTheme.colors.text.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'none';
                  e.currentTarget.style.color = travelieTheme.colors.text.secondary;
                }}
              >
                <svg style={{ width: '20px', height: '20px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: `${travelieTheme.spacing['4xl']} ${travelieTheme.spacing['2xl']}`,
          background: travelieTheme.colors.neutral.card,
          borderRadius: travelieTheme.components.card.borderRadius,
          boxShadow: travelieTheme.components.card.shadow
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto',
            marginBottom: travelieTheme.spacing.lg,
            borderRadius: '50%',
            background: travelieTheme.colors.primary.light,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg style={{ width: '40px', height: '40px', color: travelieTheme.colors.primary.main }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h3 style={{
            fontSize: travelieTheme.typography.fontSize.lg,
            fontWeight: travelieTheme.typography.fontWeight.semibold,
            color: travelieTheme.colors.text.primary,
            marginBottom: travelieTheme.spacing.sm,
            margin: `0 0 ${travelieTheme.spacing.sm} 0`
          }}>
            {searchTerm ? 'No clients found' : 'No clients yet'}
          </h3>
          <p style={{
            color: travelieTheme.colors.text.secondary,
            fontSize: travelieTheme.typography.fontSize.sm,
            marginBottom: searchTerm ? 0 : travelieTheme.spacing.lg,
            margin: `0 0 ${searchTerm ? 0 : travelieTheme.spacing.lg} 0`
          }}>
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Get started by adding your first client'}
          </p>
          {!searchTerm && (
            <button
              onClick={handleCreateClient}
              style={{
                padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.lg}`,
                background: travelieTheme.colors.primary.main,
                color: '#FFFFFF',
                border: 'none',
                borderRadius: travelieTheme.components.button.primary.borderRadius,
                fontSize: travelieTheme.typography.fontSize.sm,
                fontWeight: travelieTheme.typography.fontWeight.medium,
                cursor: 'pointer',
                transition: `background ${travelieTheme.transitions.fast}`
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.primary.hover}
              onMouseLeave={(e) => e.currentTarget.style.background = travelieTheme.colors.primary.main}
            >
              Add Your First Client
            </button>
          )}
        </div>
      ) : (
        <div style={{
          background: travelieTheme.colors.neutral.card,
          boxShadow: travelieTheme.components.card.shadow,
          overflow: 'hidden',
          borderRadius: travelieTheme.components.card.borderRadius
        }}>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0
          }}>
            {filteredClients.map((client) => (
              <li key={client.id} style={{
                borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`,
                transition: `background ${travelieTheme.transitions.fast}`,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = travelieTheme.colors.neutral.hover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  padding: travelieTheme.spacing.lg,
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'auto 1fr auto',
                  gap: travelieTheme.spacing.lg,
                  alignItems: 'center'
                }}>
                  {/* Avatar */}
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${travelieTheme.colors.primary.main} 0%, ${travelieTheme.colors.primary.hover} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: travelieTheme.typography.fontSize.lg,
                    fontWeight: travelieTheme.typography.fontWeight.semibold,
                    color: '#FFFFFF',
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    {client.first_name?.[0]?.toUpperCase()}{client.last_name?.[0]?.toUpperCase()}
                  </div>

                  {/* Client Info */}
                  <div style={{
                    flex: 1,
                    minWidth: 0
                  }}>
                    <div style={{ marginBottom: travelieTheme.spacing.xs }}>
                      <p style={{
                        fontWeight: travelieTheme.typography.fontWeight.semibold,
                        color: travelieTheme.colors.text.primary,
                        margin: 0,
                        fontSize: travelieTheme.typography.fontSize.base,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {client.first_name} {client.last_name}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.md, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.xs }}>
                        <svg style={{ width: '14px', height: '14px', color: travelieTheme.colors.text.secondary, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span style={{
                          fontSize: travelieTheme.typography.fontSize.sm,
                          color: travelieTheme.colors.text.secondary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {client.email}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: travelieTheme.spacing.xs }}>
                        <svg style={{ width: '14px', height: '14px', color: travelieTheme.colors.text.secondary, flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span style={{
                          fontSize: travelieTheme.typography.fontSize.sm,
                          color: travelieTheme.colors.text.secondary
                        }}>
                          {client.phone}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: travelieTheme.spacing.sm,
                    flexShrink: 0,
                    flexWrap: 'wrap',
                    justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    alignItems: 'center'
                  }}>
                    {/* Payment Request Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRequestPayment(client);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.xs,
                        padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                        borderRadius: travelieTheme.components.button.primary.borderRadius,
                        border: 'none',
                        background: 'transparent',
                        color: travelieTheme.colors.status.success,
                        cursor: 'pointer',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        transition: `all ${travelieTheme.transitions.fast}`,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = travelieTheme.colors.status.successLight;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      <span>Payment</span>
                    </button>

                    {/* Communication Log Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCommLoggerClient(client);
                        setShowCommLogger(true);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.xs,
                        padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                        borderRadius: travelieTheme.components.button.primary.borderRadius,
                        border: 'none',
                        background: 'transparent',
                        color: '#6366F1',
                        cursor: 'pointer',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        transition: `all ${travelieTheme.transitions.fast}`,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#EEF2FF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                      <span>Log</span>
                    </button>

                    {/* Edit Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditClient(client);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.xs,
                        padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                        borderRadius: travelieTheme.components.button.primary.borderRadius,
                        border: 'none',
                        background: 'transparent',
                        color: travelieTheme.colors.primary.main,
                        cursor: 'pointer',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        transition: `all ${travelieTheme.transitions.fast}`,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = travelieTheme.colors.primary.light;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span>Edit</span>
                    </button>

                    {/* Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClient(client.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: travelieTheme.spacing.xs,
                        padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`,
                        borderRadius: travelieTheme.components.button.primary.borderRadius,
                        border: 'none',
                        background: 'transparent',
                        color: travelieTheme.colors.status.error,
                        cursor: 'pointer',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        fontWeight: travelieTheme.typography.fontWeight.medium,
                        transition: `all ${travelieTheme.transitions.fast}`,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#FEE2E2';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <svg style={{ width: '16px', height: '16px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Client Registration Link */}
      <div style={{ marginTop: travelieTheme.spacing['3xl'] }}>
        <ClientRegistrationLink />
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: travelieTheme.spacing.md, zIndex: 1000 }} onClick={() => setShowModal(false)}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "768px", width: "100%", maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: `${travelieTheme.spacing.lg} ${travelieTheme.spacing.xl}`, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary, margin: 0 }}>
                {editingClient ? 'Edit Client' : 'Add New Client'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: "none", border: "none", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: `color ${travelieTheme.transitions.fast}` }}
                onMouseEnter={(e) => e.currentTarget.style.color = travelieTheme.colors.text.primary}
                onMouseLeave={(e) => e.currentTarget.style.color = travelieTheme.colors.text.secondary}
              >
                <svg style={{ width: "24px", height: "24px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div style={{ padding: travelieTheme.spacing.xl }}>
              <ClientFormComponent
                isEdit={!!editingClient}
                initialData={editingClient}
                onSubmit={handleSubmitClient}
                onCancel={() => setShowModal(false)}
                formLoading={formLoading}
              />
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showImportModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", overflowY: "auto", height: "100%", width: "100%", zIndex: 1000 }}>
          <div style={{ position: "relative", top: "80px", margin: "0 auto", padding: travelieTheme.spacing.lg, border: `1px solid ${travelieTheme.colors.neutral.border}`, width: isMobile ? "91.666667%" : "75%", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", borderRadius: travelieTheme.components.card.borderRadius, background: "#FFFFFF" }}>
            <div style={{ marginTop: travelieTheme.spacing.md }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: travelieTheme.spacing.md }}>
                <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>
                  Import Clients from CSV
                </h3>
                <button
                  onClick={resetImportModal}
                  style={{ color: "#9CA3AF", cursor: "pointer", transition: `color ${travelieTheme.transitions.fast}` }}
                >
                  <svg style={{ width: "24px", height: "24px" }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {!importResults ? (
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
                  {/* Template Download */}
                  <div style={{ background: "#DBEAFE", padding: travelieTheme.spacing.md, borderRadius: travelieTheme.components.card.borderRadius }}>
                    <div style={{ display: "flex", alignItems: "flex-start" }}>
                      <div style={{ flexShrink: 0 }}>
                        <svg style={{ height: "20px", width: "20px", color: "#60A5FA" }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div style={{ marginLeft: travelieTheme.spacing.md, flex: 1 }}>
                        <h4 style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: "#1E40AF" }}>Supported CSV Formats</h4>
                        <div style={{ marginTop: travelieTheme.spacing.sm, fontSize: travelieTheme.typography.fontSize.sm, color: "#1D4ED8" }}>
                          <p style={{ marginBottom: travelieTheme.spacing.sm }}>This importer supports two CSV formats:</p>
                          <ul style={{ listStyleType: "disc", listStylePosition: "inside", display: "flex", flexDirection: "column", gap: travelieTheme.spacing.xs, marginLeft: travelieTheme.spacing.sm }}>
                            <li><strong>TPCRM Format:</strong> first_name, last_name, email, phone (+ optional fields)</li>
                            <li><strong>WordPress Legacy:</strong> member_1_name, email, phone, member_2_name, etc.</li>
                          </ul>
                          <p style={{ marginTop: travelieTheme.spacing.sm }}>The system will automatically detect which format you're using.</p>
                        </div>
                        <p style={{ fontSize: travelieTheme.typography.fontSize.sm, color: "#1D4ED8", marginTop: travelieTheme.spacing.xs }}>
                          Download the CSV template with the correct format. Required fields: First Name, Last Name, Email, Phone.
                        </p>
                        <button
                          onClick={handleDownloadTemplate}
                          style={{...getButtonStyle("primary", "small"), marginTop: travelieTheme.spacing.sm}}
                        >
                          Download Template
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label style={labelStyle}>
                      Select CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      style={{
                        display: 'block',
                        width: '100%',
                        fontSize: travelieTheme.typography.fontSize.sm,
                        color: travelieTheme.colors.text.secondary,
                        cursor: 'pointer'
                      }}
                    />
                  </div>

                  {/* CSV Preview */}
                  {csvPreview.length > 0 && (
                    <div>
                      <h4 style={{...labelStyle, marginBottom: travelieTheme.spacing.sm}}>Preview (First 5 rows)</h4>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ minWidth: "100%", fontSize: travelieTheme.typography.fontSize.sm }}>
                          <thead style={{ background: "#F9FAFB" }}>
                            <tr>
                              {Object.keys(csvPreview[0] || {}).map((header) => (
                                <th key={header} style={{ padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`, textAlign: "left", fontSize: travelieTheme.typography.fontSize.xs, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.secondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody style={{ background: "#FFFFFF", borderTop: `1px solid ${travelieTheme.colors.neutral.border}` }}>
                            {csvPreview.map((row, index) => (
                              <tr key={index}>
                                {Object.values(row).map((value, cellIndex) => (
                                  <td key={cellIndex} style={{ padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`, whiteSpace: "nowrap", fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.primary }}>
                                    {value}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.md, paddingTop: travelieTheme.spacing.lg }}>
                    <button
                      onClick={resetImportModal}
                      style={getButtonStyle("secondary")}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleImportCSV}
                      disabled={!csvFile || importLoading}
                      style={getButtonStyle("success")}
                    >
                      {importLoading ? 'Importing...' : 'Import Clients'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Import Results */
                <div style={{ display: "flex", flexDirection: "column", gap: travelieTheme.spacing.lg }}>
                  <div style={{ background: "#DCFCE7", padding: travelieTheme.spacing.md, borderRadius: travelieTheme.components.card.borderRadius }}>
                    <div style={{ display: "flex" }}>
                      <div style={{ flexShrink: 0 }}>
                        <svg style={{ height: "20px", width: "20px", color: "#4ADE80" }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div style={{ marginLeft: travelieTheme.spacing.md }}>
                        <h3 style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: "#166534" }}>
                          Import Complete
                        </h3>
                        <div style={{ marginTop: travelieTheme.spacing.sm, fontSize: travelieTheme.typography.fontSize.sm, color: "#15803D" }}>
                          <p>Successfully imported {importResults.summary.successful} clients</p>
                          {importResults.results && importResults.results.length > 0 && (
                            <p>Total family members imported: {importResults.results.reduce((total, result) => total + (result.additionalMembersCount || 0), 0)}</p>
                          )}
                          {importResults.summary.failed > 0 && (
                            <p>Failed to import {importResults.summary.failed} clients</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Error Details */}
                  {importResults.errors && importResults.errors.length > 0 && (
                    <div>
                      <h4 style={{ fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium, color: "#B91C1C", marginBottom: travelieTheme.spacing.sm }}>Import Errors:</h4>
                      <div style={{ background: "#FEE2E2", padding: travelieTheme.spacing.sm, borderRadius: travelieTheme.components.card.borderRadius, maxHeight: "160px", overflowY: "auto" }}>
                        {importResults.errors.map((error, index) => (
                          <div key={index} style={{ fontSize: travelieTheme.typography.fontSize.sm, color: "#B91C1C", marginBottom: travelieTheme.spacing.xs }}>
                            Row {error.row}: {error.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stage Move Modal */}
      {showStageModal && selectedClient && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: travelieTheme.spacing.md, zIndex: 1000 }}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "448px", width: "100%" }}>
            <div style={{ padding: `${travelieTheme.spacing.lg} ${travelieTheme.spacing.xl}`, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
              <h3 style={{ fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.medium, color: travelieTheme.colors.text.primary }}>
                Move Client to New Stage
              </h3>
              <p style={{ fontSize: travelieTheme.typography.fontSize.sm, color: travelieTheme.colors.text.secondary, marginTop: travelieTheme.spacing.xs }}>
                {selectedClient.first_name} {selectedClient.last_name}
              </p>
            </div>
            <div style={{ padding: travelieTheme.spacing.xl }}>
              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <label style={labelStyle}>
                  Current Stage
                </label>
                <div 
                  style={{ display: "inline-flex", alignItems: "center", padding: `${travelieTheme.spacing.xs} ${travelieTheme.spacing.sm}`, borderRadius: "9999px", fontSize: travelieTheme.typography.fontSize.sm, fontWeight: travelieTheme.typography.fontWeight.medium }}
                  style={{ 
                    backgroundColor: `${selectedClient.workflow_stage_color}20`,
                    color: selectedClient.workflow_stage_color,
                    borderColor: selectedClient.workflow_stage_color,
                    border: '1px solid'
                  }}
                >
                  {selectedClient.workflow_stage_name || 'No Stage'}
                </div>
              </div>
              
              <div style={{ marginBottom: travelieTheme.spacing.lg }}>
                <label style={labelStyle}>
                  New Stage
                </label>
                <select
                  id="newStage"
                  style={inputStyle}
                  defaultValue=""
                >
                  <option value="">Select a stage...</option>
                  {workflowStages
                    .filter(stage => stage.id !== selectedClient.workflow_stage_id)
                    .map((stage) => (
                      <option key={stage.id} value={stage.id}>
                        {stage.name}
                      </option>
                    ))}
                </select>
              </div>

              <div style={{ marginBottom: travelieTheme.spacing.xl }}>
                <label style={labelStyle}>
                  Reason (Optional)
                </label>
                <textarea
                  id="moveReason"
                  rows="3"
                  style={inputStyle}
                  placeholder="Why are you moving this client to a new stage?"
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.md }}>
                <button
                  onClick={() => {
                    setShowStageModal(false);
                    setSelectedClient(null);
                  }}
                  style={getButtonStyle("secondary")}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const newStageId = document.getElementById('newStage').value;
                    const reason = document.getElementById('moveReason').value;
                    if (newStageId) {
                      handleMoveClientStage(selectedClient.id, parseInt(newStageId), reason || null);
                    }
                  }}
                  style={getButtonStyle("primary")}
                >
                  Move Client
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Communication Logger Modal */}
      {showCommLogger && commLoggerClient && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: travelieTheme.spacing.md, zIndex: 1000 }}>
          <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "672px", width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
            <CommunicationLogger
              clientId={commLoggerClient.id}
              onClose={() => {
                setShowCommLogger(false);
                setCommLoggerClient(null);
              }}
              onSaved={() => {
                setShowCommLogger(false);
                setCommLoggerClient(null);
                setSuccess('Communication logged successfully');
                setTimeout(() => setSuccess(null), 3000);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientsManagement;
